import { FC, ReactElement, useContext, useMemo } from 'react';
import { Stack } from '@mui/material';
import styled from 'styled-components';
import {
  AuthenticationContext,
  useGetActiveHeroConversations,
  AssociateApprovalStatus,
  useGetRetailerStoreOpeningHours,
} from '@vs-online-only-app/state';
import { useNetworkConnectionStatus } from '@vs-online-only-app/ui';
import {
  AssociateDeclinedApplicationPlaceholder,
  NetworkConnectionDownPlaceholder,
  StoreClosedPlaceholder,
} from '../placeholders';
import AssociatePendingApplicationPlaceholder from '../placeholders/AssociatePendingApplicationPlaceholder';
import AssociateReadyToChatPlaceholder from '../placeholders/AssociateReadyToChatPlaceholder';
import AssociateOfflinePlaceholder from '../placeholders/AssociateOfflinePlaceholder';

const Container = styled(Stack)`
  flex-direction: column;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.palette.background.paper};
  position: relative;
  padding: ${({ theme }) => theme.spacing(2)};
`;

const Card = styled(Stack)`
  background-color: ${({ theme }) => theme.palette.background.default};
  padding: ${({ theme }) => theme.spacing(3)};
  max-width: 400px;
  align-items: center;
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2}px;
`;

export enum PlaceholderVariant {
  STORE_CLOSED = 'storeClosed',
  ASSOCIATE_OFFLINE = 'associateOffline',
  ASSOCIATE_READY_TO_CHAT = 'associateReadyToChat',
  ASSOCIATE_APPLICATION_PENDING = 'associateApplicationPending',
  ASSOCIATE_APPLICATION_DECLINED = 'associateApplicationDeclined',
  NETWORK_CONNECTION_DOWN = 'networkConnectionDown',
}

const SelectedConversationPlaceholder: FC = () => {
  const { user } = useContext(AuthenticationContext);
  const { data: store } = useGetRetailerStoreOpeningHours(
    user?.storeId as string,
  );
  const { data: activeConversations } = useGetActiveHeroConversations();
  const isOnline = useNetworkConnectionStatus();

  const PlaceholderMap: Record<PlaceholderVariant, ReactElement> = {
    [PlaceholderVariant.ASSOCIATE_APPLICATION_PENDING]: (
      <AssociatePendingApplicationPlaceholder />
    ),
    [PlaceholderVariant.ASSOCIATE_APPLICATION_DECLINED]: (
      <AssociateDeclinedApplicationPlaceholder />
    ),
    [PlaceholderVariant.ASSOCIATE_READY_TO_CHAT]: (
      <AssociateReadyToChatPlaceholder
        hasActiveConversations={Boolean(activeConversations?.length)}
      />
    ),
    [PlaceholderVariant.STORE_CLOSED]: <StoreClosedPlaceholder />,
    [PlaceholderVariant.ASSOCIATE_OFFLINE]: <AssociateOfflinePlaceholder />,
    [PlaceholderVariant.NETWORK_CONNECTION_DOWN]: (
      <NetworkConnectionDownPlaceholder />
    ),
  };

  const variant = useMemo(() => {
    if (!isOnline) {
      return PlaceholderVariant.NETWORK_CONNECTION_DOWN;
    }
    if (!user) {
      return null;
    }
    const isAssociateAvailable = Boolean(user.available);
    const isAssociateApplicationPending =
      user.approvalStatus === AssociateApprovalStatus.Pending;
    const isAssociateApplicationDeclined =
      user.approvalStatus === AssociateApprovalStatus.Declined;

    if (isAssociateApplicationPending) {
      return PlaceholderVariant.ASSOCIATE_APPLICATION_PENDING;
    }
    if (isAssociateApplicationDeclined) {
      return PlaceholderVariant.ASSOCIATE_APPLICATION_DECLINED;
    }

    if (!store) {
      return null;
    }
    const isStoreClosed = !store.businessOpen;

    if (isStoreClosed) {
      return PlaceholderVariant.STORE_CLOSED;
    }
    if (!isAssociateAvailable) {
      return PlaceholderVariant.ASSOCIATE_OFFLINE;
    }

    return PlaceholderVariant.ASSOCIATE_READY_TO_CHAT;
  }, [user, store, isOnline]);

  if (!variant) {
    return null;
  }

  return (
    <Container>
      <Card>{PlaceholderMap[variant]}</Card>
    </Container>
  );
};

export default SelectedConversationPlaceholder;
